import type { User } from '@/features/authorization/api/models'
import { CACHE_KEY_USE_FETCH_USER } from '@/features/authorization/api/useFetchUser'
import { MissionBadge } from '@/features/missions/components/MissionBadge'
import { RecordingStateIdle } from '@/features/nectar/components/audio-recording/RecordingStateIdle'
import { RecordingStateRecorded } from '@/features/nectar/components/audio-recording/RecordingStateRecorded'
import { RecordingStateRecording } from '@/features/nectar/components/audio-recording/RecordingStateRecording'
import { TaskReportModal } from '@/features/nectar/components/TaskReportModal'
import { useTurnstileCaptcha } from '@/features/turnstile'
import { Button } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { Flag } from 'lucide-react'
import { useState } from 'react'
import { useNectarTask } from '../../api'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'
import { useRecordingQualityAnalytics } from '../../hooks/useRecordingQualityAnalytics'
import { useSubmitAudioRecording } from '../../hooks/useSubmitAudioRecording'
import type { TaskRendererProps } from '../task-types'

export const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

export const AudioRecordingTask = ({
  taskId,
  taskIndex,
  projectId,
  captchaEnabled,
  taskReportingEnabled,
  onSubmitted
}: TaskRendererProps) => {
  const { data: task } = useNectarTask(projectId, taskId)
  console.log('task', task, taskId, projectId)
  
  const recorder = useAudioRecorder({ captureMetadata: true })
  const captcha = useTurnstileCaptcha({ enabled: captchaEnabled, action: 'submission' })
  const submitAudioRecording = useSubmitAudioRecording({ recorder, captcha })
  const [reportModalOpen, setReportModalOpen] = useState(false)

  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<User>([CACHE_KEY_USE_FETCH_USER])

  const { state, seconds, audioUrl, blob, startRecording, stopRecording, reset, quality, analyser } = recorder
  useRecordingQualityAnalytics(projectId, quality)

  const phrase = typeof task?.params?.phrase === 'string' ? task?.params.phrase : ''
  const emotion =
    typeof task?.params?.emotion === 'string'
      ? task?.params.emotion
      : typeof task?.metadata?.emotion === 'string'
        ? task?.metadata.emotion
        : null

  const onSubmit = () => {
    if (!blob) return
    submitAudioRecording({
      projectId,
      taskId: taskId,
      taskIndex,
      blob,
      seconds,
      onSuccess: () => onSubmitted(taskId)
    })
  }

  const isRecordedState = state === 'recorded' || state === 'submitting' || state === 'submitted'
  const submitDisabled = captcha.active && !captcha.token
  const hasHtmlInstruction = typeof task?.instruction === 'string' && /<\/?[a-z][\s\S]*>/i.test(task?.instruction)
  const showReportButton = taskReportingEnabled && !!currentUser

  return (
    <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      {task?.instruction &&
        (hasHtmlInstruction ? (
          <div
            className="text-body-2 text-gray-80 bg-orange/15 m-auto mb-3 block rounded-xl p-4 leading-5"
            dangerouslySetInnerHTML={{ __html: task.instruction }}
          />
        ) : (
          <div className="text-body-2 text-gray-80 m-auto mb-3 block rounded-xl border border-solid border-gray-100 p-4 text-center leading-5">
            {task.instruction}
          </div>
        ))}
      <div className="relative flex flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
        {showReportButton && (
          <div className="absolute top-1 right-2 flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="small"
              className="text-gray-90! hover:text-orange! border-none!"
              onClick={() => setReportModalOpen(true)}
            >
              <Flag size={14} className="mr-1.5 shrink-0" />
              <Trans>Report</Trans>
            </Button>
          </div>
        )}
        {phrase && (
          <div className="text-body-2 text-gray-80 -mb-2">
            <Trans>Read the text below exactly as written</Trans>
          </div>
        )}
        {phrase && (
          <span className="text-h5 block rounded-xl p-4 text-center leading-[120%]">&ldquo;{phrase}&rdquo;</span>
        )}
        {emotion && (
          <div className="mt-4 flex">
            <MissionBadge variant="warning">{emotion}</MissionBadge>
          </div>
        )}
        <div className="flex w-full flex-col items-center gap-5">
          {state === 'idle' && <RecordingStateIdle onStartRecording={startRecording} />}
          {state === 'recording' && (
            <RecordingStateRecording seconds={seconds} analyser={analyser} onStopRecording={stopRecording} />
          )}
          {isRecordedState && (
            <>
              {captcha.renderWidget('w-full')}
              <RecordingStateRecorded
                audioUrl={audioUrl}
                seconds={seconds}
                isSubmitting={state === 'submitting'}
                submitDisabled={submitDisabled}
                quality={quality}
                onReRecord={() => {
                  captcha.reset()
                  reset()
                  startRecording()
                }}
                onSubmit={onSubmit}
              />
            </>
          )}
        </div>
      </div>

      {showReportButton && currentUser && (
        <TaskReportModal
          open={reportModalOpen}
          onClose={() => setReportModalOpen(false)}
          projectId={projectId}
          taskId={taskId}
          userId={currentUser.id}
        />
      )}
    </div>
  )
}
